import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import { continueRender, delayRender, useCurrentFrame } from "remotion";
import { MAP_STYLE_URLS } from "constants/mapStyles";
import { MAPBOX_CSS } from "./mapboxCss.generated";

// The live Stippl map (the app's own style) as a stage. The camera is set synchronously every
// frame so overlays can project real coordinates for the same frame; the frame is only captured
// once Mapbox reports idle (tiles loaded and drawn), the same gate the trip reel needs.
//
// No access token here: this bundle's token is set once, by the trip reel (TikTokVideoVanilla.tsx),
// and the city guide relies on it the same way. mapbox-gl.css is not imported (see mapboxCss.generated.ts):
// the few rules the film needs, the canvas position and the Mapbox logo, come scoped with the stage.
export const STIPPL_STYLE = MAP_STYLE_URLS.stippl;

export type Cam = { lng: number; lat: number; zoom: number; pitch: number; bearing: number };
export type Project = (lng: number, lat: number) => { x: number; y: number };

export const MapStage: React.FC<{
	w: number;
	h: number;
	cam: Cam;
	padding?: { top: number; bottom: number; left: number; right: number };
	overlay?: (project: Project) => React.ReactNode;
	style?: React.CSSProperties;
	mapOpacity?: number;
}> = ({ w, h, cam, padding, overlay, style, mapOpacity = 1 }) => {
	const frame = useCurrentFrame();
	const box = useRef<HTMLDivElement>(null);
	const mapRef = useRef<mapboxgl.Map | null>(null);
	const [ready, setReady] = useState(false);
	const [loadHandle] = useState(() => delayRender("mapbox style", { timeoutInMilliseconds: 120000 }));

	useEffect(() => {
		if (!box.current) return undefined;
		const map = new mapboxgl.Map({
			container: box.current,
			style: STIPPL_STYLE,
			center: [cam.lng, cam.lat],
			zoom: cam.zoom,
			pitch: cam.pitch,
			bearing: cam.bearing,
			interactive: false,
			attributionControl: false,
			preserveDrawingBuffer: true,
			fadeDuration: 0,
			antialias: true,
			projection: { name: "globe" },
			maxTileCacheSize: 80, // a long camera flight otherwise fills the renderer's memory
		} as mapboxgl.MapboxOptions);
		// Mapbox measures its box on screen and divides by the first scale transform it meets up the tree.
		// Inside a scaled parent inside a scaled parent (the ads' screen dive) that guess is off by the outer
		// scale and the map renders S times too big. This map measures its layout box instead, which is the
		// size we mean, transformed or not; resize() then redraws it at that size.
		const dims = map as unknown as { _updateContainerDimensions: () => void; _containerWidth: number; _containerHeight: number };
		// a private method of mapbox-gl (^3.2.0): an upgrade that renames it would silently bring the S-times-too-big map back
		if (typeof dims._updateContainerDimensions !== "function") {
			throw new Error(
				"mapbox-gl no longer has _updateContainerDimensions: MapStage's nested-scale fix for the ads' screen dive needs redoing for this version"
			);
		}
		dims._updateContainerDimensions = () => {
			dims._containerWidth = box.current?.offsetWidth || w;
			dims._containerHeight = box.current?.offsetHeight || h;
		};
		map.resize();
		mapRef.current = map;
		map.on("style.load", () => {
			// the globe floats on the video's own mint, no stars
			map.setFog({
				color: "rgb(237,249,244)",
				"high-color": "rgb(214,240,226)",
				"horizon-blend": 0.06,
				"space-color": "rgb(237,249,244)",
				"star-intensity": 0,
			});
		});
		map.on("load", () => {
			map.resize();
			setReady(true);
			continueRender(loadHandle);
		});
		return () => map.remove();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const map = mapRef.current;
	if (map && ready) {
		map.jumpTo({
			center: [cam.lng, cam.lat],
			zoom: cam.zoom,
			pitch: cam.pitch,
			bearing: cam.bearing,
			padding: padding ?? { top: 0, bottom: 0, left: 0, right: 0 },
		});
	}
	const project: Project = (lng, lat) => {
		if (!map || !ready) return { x: -9999, y: -9999 };
		const p = map.project([lng, lat]);
		return { x: p.x, y: p.y };
	};

	useLayoutEffect(() => {
		if (!map || !ready) return;
		const handle = delayRender(`map frame ${frame}`, { timeoutInMilliseconds: 120000 });
		let done = false;
		const start = Date.now();
		const finish = () => {
			if (done) return;
			done = true;
			continueRender(handle);
		};
		// after the camera change has been drawn, wait until every tile is in; never longer than 20 s
		const check = () => {
			if (done) return;
			if (map.loaded() && map.areTilesLoaded()) requestAnimationFrame(() => requestAnimationFrame(finish));
			else if (Date.now() - start > 20000) finish();
			else setTimeout(check, 40);
		};
		map.once("idle", finish);
		map.once("render", check);
		// a hard way out: if the map never draws again (lost context), still hand the frame over
		const hard = setTimeout(finish, 25000);
		map.once("idle", () => clearTimeout(hard));
		map.triggerRepaint();
	}, [frame, ready, map]);

	return (
		<div data-map-stage style={{ position: "absolute", left: 0, top: 0, width: w, height: h, ...style }}>
			<style>{MAPBOX_CSS}</style>
			<div ref={box} style={{ position: "absolute", left: 0, top: 0, width: w, height: h, opacity: mapOpacity }} />
			{ready && overlay ? overlay(project) : null}
		</div>
	);
};
